import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

// Main-process fallback; the server applies the same membership scope as the inbox.
// Фоновий запит основного процесу з тими самими правами, що й у списку чатів.
export const DESKTOP_UNREAD_COUNT_PATH = "/rpc/notifications/unreadCount";
export const UnreadChatCountSchema = z.object({ count: z.number().int().nonnegative() });

/*
 * Notifications across a person's devices.
 *
 * A person may use HrokBot on several devices. For a bot's completion, failure, help request or
 * takeover request the server notifies by one rule: if the chat is active on any one device,
 * no device is notified; otherwise every registered device is. "Active" means the chat (direct
 * or group) is open on that device while its window is visible and focused.
 *
 * A client therefore does three things:
 *   1. registers itself once per sign-in (`notifications.registerDevice`);
 *   2. reports what it is showing while that is true (`notifications.reportPresence`);
 *   3. a desktop app also keeps `notifications.subscribe` open and shows what arrives.
 */

/**
 * A device id is chosen by the client: a random value (a UUID is fine) created on first launch
 * and kept across launches and sign-ins, so registering again updates the same device. It is
 * scoped to the signed-in account; two accounts can use the same id without clashing.
 */
export const NOTIFICATION_DEVICE_ID_PATTERN = /^[A-Za-z0-9._:-]{8,128}$/;
export const NotificationDeviceId = z.string().regex(NOTIFICATION_DEVICE_ID_PATTERN);

/** A presence report counts for this long. A device that stops reporting stops suppressing. */
export const NOTIFICATION_PRESENCE_TTL_MS = 60_000;
/** How often a device repeats its presence report while the chat stays visible and focused. */
export const NOTIFICATION_PRESENCE_REFRESH_MS = 25_000;
/** A desktop notice waits this long for an app to read it. Later than that it is gone. */
export const DESKTOP_NOTICE_TTL_MS = 120_000;
/**
 * A desktop device counts as connected while its stream has beaten within this window. It is
 * longer than three heartbeats, so a brief reconnect does not drop the device.
 */
export const DESKTOP_DEVICE_LIVE_MS = 90_000;

/** The notification itself, as the phone push carries it and as the desktop stream delivers it. */
export const NotificationMessageSchema = z.object({
  kind: z.enum(["completion", "failure", "help", "takeover"]),
  title: z.string(),
  body: z.string(),
  botId: Id,
  threadId: Id,
  /** Set for a group chat, so that opening the notice does not land in the bot's direct chat. */
  groupId: Id.optional(),
  /** Unread chats to show on the app icon. Absent: leave the badge as it is. */
  badge: z.number().int().nonnegative().optional(),
});
export type NotificationMessage = z.infer<typeof NotificationMessageSchema>;

/**
 * A standard Push API subscription, as `PushSubscription.toJSON()` returns it: an https endpoint
 * and the two keys of the browser. The server keeps it as given for the sender that will use it
 * and never returns it from any call, not even to its owner; it is not logged.
 */
export const WebPushSubscriptionSchema = z.object({
  endpoint: z.url({ protocol: /^https$/ }).max(2048),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z
      .string()
      .regex(/^[A-Za-z0-9_-]+={0,2}$/)
      .max(128),
    auth: z
      .string()
      .regex(/^[A-Za-z0-9_-]+={0,2}$/)
      .max(64),
  }),
});
export type WebPushSubscription = z.infer<typeof WebPushSubscriptionSchema>;

/**
 * `expo`: a phone; `token` is its Expo push token. `webpush`: an installed web app on a phone
 * or another device; `subscription` is its Push API subscription. `desktop`: an app that keeps
 * `notifications.subscribe` open. Registering the same device id again replaces the device, so
 * call it on every launch and after every sign-in. A token or endpoint belongs to one device of
 * one account: registering it takes it from any other.
 *
 * Every kind is treated alike by the notification rule. A `webpush` device is sent to through
 * the standard Push API with the deployment's VAPID key, the same way an Expo device is sent to
 * through Expo.
 */
export const RegisterNotificationDeviceInput = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("expo"),
    deviceId: NotificationDeviceId,
    token: z.string().min(8).max(512).regex(/^\S+$/),
  }),
  z.object({
    kind: z.literal("webpush"),
    deviceId: NotificationDeviceId,
    subscription: WebPushSubscriptionSchema,
  }),
  z.object({ kind: z.literal("desktop"), deviceId: NotificationDeviceId }),
]);
export type RegisterNotificationDeviceInput = z.infer<typeof RegisterNotificationDeviceInput>;

/**
 * Call before signing out. The server also drops a device when the session that registered it
 * ends, but a client that is still signed in is the only one that knows it is leaving.
 */
export const UnregisterNotificationDeviceInput = z.object({ deviceId: NotificationDeviceId });
export type UnregisterNotificationDeviceInput = z.infer<typeof UnregisterNotificationDeviceInput>;

/** A chat is addressed the way threads are: by bot for a direct chat, by group for a group chat. */
export const PresenceChat = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("bot"), id: Id }),
  z.object({ kind: z.literal("group"), id: Id }),
]);
export type PresenceChat = z.infer<typeof PresenceChat>;

/**
 * `chat` is the chat this device shows right now with its window visible and focused, or null
 * when it shows none. Send it when that changes and again every
 * `NOTIFICATION_PRESENCE_REFRESH_MS` while it holds. The report expires after
 * `NOTIFICATION_PRESENCE_TTL_MS`, so a client that dies leaves nothing behind. A device that
 * is not registered for notifications may still report: it silences the others all the same.
 *
 * The chat is looked up like any thread request: in the Space the request names (the
 * `x-rakazo-space-id` header every other call carries), and only if the person can open it.
 * A chat of another Space, or one the person cannot open, is refused and nothing is recorded.
 */
export const ReportDevicePresenceInput = z.object({
  deviceId: NotificationDeviceId,
  chat: PresenceChat.nullable(),
});
export type ReportDevicePresenceInput = z.infer<typeof ReportDevicePresenceInput>;

/**
 * `deviceId` must be a registered desktop device; an unknown one is refused with NOT_FOUND, so
 * register again and reopen. `cursor` is the cursor of the last frame the app received; send it
 * when reconnecting and omit it on the first connection.
 *
 * The stream carries the notices of every Space the person belongs to, so open it with the same
 * `x-rakazo-space-id` header as the rest of the app. It ends with UNAUTHORIZED once the session
 * is signed out or revoked, or the person leaves that Space (the server re-checks every 10 s
 * while frames flow, and at each heartbeat). A server heartbeat arrives every 20 s. Treat three
 * heartbeats of silence (60 s) as a dead socket, and on that or on any error reconnect with the
 * last cursor, backing off. A notice not read within `DESKTOP_NOTICE_TTL_MS` is gone.
 *
 * No browser API is needed: it is an ordinary HTTP request that any client holding the session
 * cookie can make, so the desktop app's main process can keep it open with its window closed.
 * `readDesktopNoticeFrames` (notification-stream.ts) reads the response, and the exact request
 * and framing are written there.
 */
export const SubscribeNotificationsInput = z.object({
  deviceId: NotificationDeviceId,
  cursor: z.number().int().nonnegative().optional(),
});
export type SubscribeNotificationsInput = z.infer<typeof SubscribeNotificationsInput>;

/**
 * Every frame carries a `cursor`. The first frame is a heartbeat, so a client has a cursor from
 * the start. The server repeats a few seconds of notices when a client resumes, so a notice can
 * arrive twice: show it once per `id`. Show a notice with `threadId` as its native notification
 * tag, so a newer one for the same chat replaces the older.
 */
export const DesktopNoticeFrameSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("heartbeat"), cursor: z.number().int().nonnegative() }),
  z.object({
    type: z.literal("notice"),
    /** The dedup key: the same notice always has the same id. */
    id: Id,
    cursor: z.number().int().nonnegative(),
    createdAt: IsoDate,
    /** The Space that owns the chat, to open the right one when the notice is clicked. */
    spaceId: Id,
    notice: NotificationMessageSchema,
  }),
]);
export type DesktopNoticeFrame = z.infer<typeof DesktopNoticeFrameSchema>;
